/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { AlertCircle, LayoutTemplate, SearchX } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { Trans, useLingui } from '@lingui/react/macro';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { deriveAsyncState } from 'shared/utils/async-state';

import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { fetchBoardsByTemplateId } from 'modules/template-editor/store/templates/actions';
import { useCatalogTemplates } from 'modules/template-editor/ui/hooks/use-catalog-templates';
import { selectCatalogTemplatesByFilter, selectFilterByName } from 'modules/template-editor/store/templates/selectors';
import { setFilterByName } from 'modules/template-editor/store/templates/templates';
import CatalogGridSkeleton from '../components/catalog/catalog-grid-skeleton';
import Header from '../components/catalog/header';
import NewTemplate from '../components/catalog/new-template';
import TemplateItem from '../components/catalog/template-item';
import ImportBoardTemplateButton from '../components/elements/import-board-template-button';
import { TemplateListCatalog } from './styles';

/** Общий «готовый» шаблон без владельца; не стартовый (не `default`) — IMGM-1589. */
const SHARED_IMPORT_TARGET = { shared: true } as const;

function Catalog() {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Трансляции`, to: Pages.TEMPLATE.INDEX },
          { label: t`Готовые шаблоны`, to: Pages.TEMPLATE.CATALOG.INDEX },
        ]}
      />
    ),
    [t],
  );

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const dispatchWithToast = useDispatchWithToast();
  const templates = useSelector(selectCatalogTemplatesByFilter);
  const filterByName = useSelector(selectFilterByName);
  const { loadedTemplates } = useTemplates();

  const isAdmin = useIsAdmin();

  const { status, retry } = useCatalogTemplates();

  // Карточка «Добавить новый шаблон» супер-админа — тоже содержимое сетки: с ней пустой каталог без поиска
  // остаётся сеткой, а не пустым состоянием. Считается только после загрузки, иначе скрыла бы скелетон.
  const hasNewTemplateCard = isAdmin && status === 'loaded' && !filterByName;
  // Ветки — из отфильтрованной выдачи: иначе «ничего не найдено» не покажется (`.claude/docs/async-loading-states.md`).
  const listState = deriveAsyncState({
    itemsCount: templates.length + (hasNewTemplateCard ? 1 : 0),
    hasCurrentData: status === 'loaded',
    isFetching: status === 'loading',
    isError: status === 'error',
  });

  useEffect(() => {
    if (templates.length > 0) {
      const promises = templates
        .filter((template) => !loadedTemplates.includes(template.id))
        .map((template) =>
          dispatchWithToast(fetchBoardsByTemplateId({ templateId: template.id }), { error: msg`Не удалось загрузить табло` }),
        );
      Promise.all(promises);
    }
  }, [dispatchWithToast, loadedTemplates, t, templates, templates.length]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'flex flex-col'} style={{ height: '100%', overflow: 'hidden' }}>
        <Header
          actions={
            isAdmin && (
              <ImportBoardTemplateButton target={SHARED_IMPORT_TARGET}>
                <Trans>Импортировать из файла</Trans>
              </ImportBoardTemplateButton>
            )
          }
        />
        <UDAsyncContent
          state={listState}
          className="flex min-h-0 flex-1"
          loading={<CatalogGridSkeleton />}
          error={
            <UDEmptyState
              icon={<AlertCircle size={28} />}
              title={<Trans>Не удалось загрузить готовые шаблоны</Trans>}
              subtitle={<Trans>Проверьте соединение и попробуйте ещё раз</Trans>}
              action={
                <UDButton variant="secondary" onClick={retry}>
                  <Trans>Повторить</Trans>
                </UDButton>
              }
            />
          }
          empty={
            filterByName ? (
              <UDEmptyState
                icon={<SearchX size={28} />}
                title={<Trans>По такому запросу ничего не найдено</Trans>}
                subtitle={<Trans>Проверьте название или сбросьте поиск</Trans>}
                action={
                  <UDButton variant="secondary" onClick={() => dispatch(setFilterByName(''))}>
                    <Trans>Сбросить поиск</Trans>
                  </UDButton>
                }
              />
            ) : (
              <UDEmptyState
                icon={<LayoutTemplate size={28} />}
                title={<Trans>Готовых шаблонов пока нет</Trans>}
                subtitle={<Trans>Соберите свой шаблон в разделе «Трансляции»</Trans>}
                action={
                  <UDButton variant="secondary" onClick={() => navigate(Pages.TEMPLATE.INDEX)}>
                    <Trans>К трансляциям</Trans>
                  </UDButton>
                }
              />
            )
          }
        >
          <SkeletonPreview skeleton={<CatalogGridSkeleton />}>
            <TemplateListCatalog>
              {templates.map((template) => (
                <TemplateItem key={template.id} template={template} />
              ))}
              {isAdmin && <NewTemplate />}
            </TemplateListCatalog>
          </SkeletonPreview>
        </UDAsyncContent>
      </div>
    </>
  );
}

export default Catalog;
